// pages/PrimaryTeacherMarksEntry.jsx
import React, { useState, useEffect } from 'react';
import {
  Container, Paper, Typography, Box, Grid, Card, CardContent,
  TextField, Button, FormControl, InputLabel, Select, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, Alert, Snackbar, Avatar, CircularProgress, Backdrop,
  Stepper, Step, StepLabel, Tabs, Tab, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import {
  Search,
  Save,
  Add,
  Delete,
  School,
  Grade,
  MenuBook,
  CheckCircle,
  Print,
  Download,
  People,
  Visibility,
  Refresh,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import api from '../services/api';

const StyledPaper = styled(Paper)(({ theme }) => ({
  borderRadius: 16,
  padding: 24,
  marginBottom: 24,
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 600,
  backgroundColor: '#1a237e',
  color: 'white',
}));

// Official subject lists by class (based on your curriculum)
const subjectsByClass = {
  'Pre-school': [
    'NAIPENDA NCHI YANGU',
    'HEALTH AND ENVIRONMENT',
    'ARITHMETIC, SCIENCE AND ICT',
    'EARL LIFE SKILLS',
    'EARLY LITERACY SKILLS',
    'CREATIVE ARTS AND SPORTS',
    'KISWAHILI',
  ],
  'STD I': [
    'LEARN ENGLISH',
    'READING',
    'WRITING',
    'ARTHMETIC',
    'APPRECIATING CULTURE, ARTS AND SPORTS',
    'CARE FOR HEALTH AND ENVIRONMENT',
    'KISWAHILI',
  ],
  'STD II': [ // same as STD I
    'LEARN ENGLISH',
    'READING',
    'WRITING',
    'ARTHMETIC',
    'APPRECIATING CULTURE, ARTS AND SPORTS',
    'CARE FOR HEALTH AND ENVIRONMENT',
    'KISWAHILI',
  ],
  'STD III': [
    'KISWAHILI',
    'ENGLISH',
    'MATHEMATICS',
    'HISTORIA YA TANZANIA NA MAADILI',
    'ARTS AND SPORTS',
    'SCIENCE',
    'GEOGRAPHY AND THE ENVIRONMENT',
  ],
  'STD IV': [ // same as STD III
    'KISWAHILI',
    'ENGLISH',
    'MATHEMATICS',
    'HISTORIA YA TANZANIA NA MAADILI',
    'ARTS AND SPORTS',
    'SCIENCE',
    'GEOGRAPHY AND THE ENVIRONMENT',
  ],
  'STD V': [ // same as STD III
    'KISWAHILI',
    'ENGLISH',
    'MATHEMATICS',
    'HISTORIA YA TANZANIA NA MAADILI',
    'ARTS AND SPORTS',
    'SCIENCE',
    'GEOGRAPHY AND THE ENVIRONMENT',
  ],
  'STD VI': [
    'KISWAHILI',
    'ENGLISH',
    'SOCIAL STUDIES',
    'VOCATIONAL STUDIES',
    'SCIENCE',
    'MATHEMATICS',
    'CIVIC AND MORAL',
  ],
  'STD VII': [ // same as STD VI
    'KISWAHILI',
    'ENGLISH',
    'SOCIAL STUDIES',
    'VOCATIONAL STUDIES',
    'SCIENCE',
    'MATHEMATICS',
    'CIVIC AND MORAL',
  ],
};

const PrimaryTeacherMarksEntry = () => {
  // Tab state
  const [activeTab, setActiveTab] = useState(0);
  
  // Enter Marks states
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchReg, setSearchReg] = useState('');
  const [student, setStudent] = useState(null);
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [marks, setMarks] = useState([]);
  const [newSubject, setNewSubject] = useState('');
  const [selectedClass, setSelectedClass] = useState('');
  const [term, setTerm] = useState('Term 1');
  const [academicYear, setAcademicYear] = useState(new Date().getFullYear().toString());
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [passwordModal, setPasswordModal] = useState(false);
  const [passwordData, setPasswordData] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [passwordError, setPasswordError] = useState('');
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  const handleChangePassword = async () => {
    if (!passwordData.current_password || !passwordData.new_password) {
      setPasswordError('Please fill all password fields');
      return;
    }
    if (passwordData.new_password !== passwordData.confirm_password) {
      setPasswordError('New passwords do not match');
      return;
    }
    if (passwordData.new_password.length < 6) {
      setPasswordError('Password must be at least 6 characters long');
      return;
    }
    try {
      await api.post('/auth/change-password', {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password,
      });
      setSnackbar({ open: true, message: 'Your password has been changed successfully!', severity: 'success' });
      setPasswordModal(false);
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
      setPasswordError('');
    } catch (err) {
      setPasswordError(err.response?.data?.error || 'Failed to change password');
    }
  };
  const [saving, setSaving] = useState(false);
  const [existingMarks, setExistingMarks] = useState([]);

  // View Results states
  const [viewSearchReg, setViewSearchReg] = useState('');
  const [viewStudent, setViewStudent] = useState(null);
  const [viewResults, setViewResults] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [selectedViewTerm, setSelectedViewTerm] = useState('');
  const [studentTerms, setStudentTerms] = useState([]);

  const steps = ['Search Student', 'Enter Marks', 'Success'];

  // Search student for marks entry
  const handleSearchStudent = async () => {
    if (!searchReg.trim()) {
      setSnackbar({ open: true, message: 'Enter registration number', severity: 'error' });
      return;
    }

    setLoading(true);
    try {
      const response = await api.get(`/primary/student`, {
        params: { registration: searchReg.trim() }
      });
      setStudent(response.data);
      setSelectedStudent(response.data);
      await loadExistingMarks(response.data.id);
      setActiveStep(1);
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Student not found',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Load existing marks for the student
  const loadExistingMarks = async (studentId) => {
    try {
      const response = await api.get('/primary/marks', {
        params: {
          student_id: studentId,
          term: term,
          year: academicYear
        }
      });
      
      if (response.data && response.data.length > 0) {
        setExistingMarks(response.data);
        const initialMarks = response.data.map(m => ({
          subject_name: m.subject_name,
          subject_id: m.subject_id,
          marks_obtained: m.marks_obtained,
          total_marks: m.total_marks,
          grade: m.grade,
          remarks: m.remarks || ''
        }));
        setMarks(initialMarks);
      } else {
        setExistingMarks([]);
        setMarks([]);
      }
    } catch (error) {
      console.error('Failed to load existing marks:', error);
      setMarks([]);
    }
  };

  // Add new subject row
  const handleAddSubject = () => {
    if (!newSubject) {
      setSnackbar({ open: true, message: 'Please select a subject', severity: 'warning' });
      return;
    }
    
    if (marks.some(m => m.subject_name === newSubject)) {
      setSnackbar({ open: true, message: 'Subject already added', severity: 'warning' });
      return;
    }
    
    setMarks([...marks, {
      subject_name: newSubject,
      subject_id: null,
      marks_obtained: '',
      total_marks: 100,
      grade: '',
      remarks: ''
    }]);
    setNewSubject('');
  };

  // Remove subject row
  const handleRemoveSubject = (index) => {
    const updatedMarks = [...marks];
    updatedMarks.splice(index, 1);
    setMarks(updatedMarks);
  };

  // Handle mark change
  const handleMarkChange = (index, field, value) => {
    const updatedMarks = [...marks];
    updatedMarks[index][field] = value;
    
    if (field === 'marks_obtained' && value) {
      const percentage = (parseFloat(value) / updatedMarks[index].total_marks) * 100;
      updatedMarks[index].grade = calculateGrade(percentage);
    }
    
    setMarks(updatedMarks);
  };

  // Calculate grade based on percentage
  const calculateGrade = (percentage) => {
    if (percentage >= 80) return 'A';
    if (percentage >= 70) return 'B';
    if (percentage >= 60) return 'C';
    if (percentage >= 50) return 'D';
    if (percentage >= 40) return 'E';
    return 'F';
  };

  // Save marks
  const handleSaveMarks = async () => {
    if (!selectedStudent) return;
    
    if (marks.length === 0) {
      setSnackbar({ open: true, message: 'Please add at least one subject', severity: 'warning' });
      return;
    }
    
    const invalidMarks = marks.filter(m => !m.marks_obtained && m.marks_obtained !== 0);
    if (invalidMarks.length > 0) {
      setSnackbar({ open: true, message: `Please enter marks for all added subjects`, severity: 'warning' });
      return;
    }

    setSaving(true);
    try {
      for (const mark of marks) {
        const payload = {
          student_id: selectedStudent.id,
          term: term,
          academic_year: academicYear,
          marks: [{
            subject_name: mark.subject_name,
            marks_obtained: parseFloat(mark.marks_obtained),
            total_marks: mark.total_marks,
            percentage: (parseFloat(mark.marks_obtained) / mark.total_marks) * 100,
            grade: mark.grade,
            remarks: mark.remarks || ''
          }]
        };

        await api.post('/primary/marks', payload);
      }
      
      setSnackbar({ open: true, message: `Marks saved successfully for ${marks.length} subject(s)!`, severity: 'success' });
      await loadExistingMarks(selectedStudent.id);
      setActiveStep(2);
    } catch (error) {
      console.error('Save error:', error);
      setSnackbar({ open: true, message: error.response?.data?.error || 'Failed to save marks', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // Get class list
  const handleGetClassList = async () => {
    if (!selectedClass) {
      setSnackbar({ open: true, message: 'Please select a class', severity: 'warning' });
      return;
    }
    
    setLoading(true);
    try {
      const response = await api.get('/primary/students', {
        params: { class: selectedClass }
      });
      setStudents(response.data);
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to load students', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Reset form
  const handleReset = () => {
    setActiveStep(0);
    setStudent(null);
    setSelectedStudent(null);
    setSearchReg('');
    setStudents([]);
    setSelectedClass('');
    setMarks([]);
    setNewSubject('');
    setTerm('Term 1');
    setAcademicYear(new Date().getFullYear().toString());
  };

  // ==================== VIEW RESULTS FUNCTIONS ====================
  
  // Search student for viewing results
  const handleViewSearch = async () => {
    if (!viewSearchReg.trim()) return;
    setViewLoading(true);
    try {
      const response = await api.get(`/primary/student`, {
        params: { registration: viewSearchReg.trim() }
      });
      setViewStudent(response.data);
      
      // Get all marks for this student
     const marksResponse = await api.get('/primary/marks', {
  params: {
    student_id: response.data.id
        }
      });
      
      const allMarks = marksResponse.data || [];
      setViewResults(allMarks);
      
      // Get unique terms from marks
      const termsSet = new Set();
      if (Array.isArray(allMarks)) {
        allMarks.forEach(mark => {
          if (mark.term) {
            termsSet.add(mark.term);
          }
        });
      }
      const termsArray = Array.from(termsSet);
      setStudentTerms(termsArray);
      if (termsArray.length > 0) {
        setSelectedViewTerm(termsArray[0]);
      }
    } catch (error) {
      setSnackbar({ open: true, message: error.response?.data?.error || 'Student not found', severity: 'error' });
      setViewStudent(null);
      setViewResults(null);
    } finally {
      setViewLoading(false);
    }
  };

  // Filter results by selected term
  const filteredResults = viewResults && Array.isArray(viewResults) 
    ? viewResults.filter(r => r.term === selectedViewTerm)
    : [];

  // Calculate summary for filtered results
const resultTotalObt = filteredResults.reduce((s, r) => s + (parseFloat(r.marks_obtained) || 0), 0);
const resultTotalPoss = filteredResults.reduce((s, r) => s + (parseFloat(r.total_marks) || 100), 0)
const resultAvgPct = resultTotalPoss > 0 ? (resultTotalObt / resultTotalPoss) * 100 : 0;

  // Get available subjects for the selected student's class (for the "Add Subject" dropdown)
  const getAvailableSubjectsForStudent = () => {
    if (!selectedStudent) return [];
    const classSubjects = subjectsByClass[selectedStudent.current_class] || [];
    return classSubjects.filter(sub => !marks.some(m => m.subject_name === sub));
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>

      {/* ── Teacher Portal Top Navigation Bar ── */}
      <Paper sx={{ mb: 3, p: 2, borderRadius: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, bgcolor: '#00695c', color: 'white' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar sx={{ bgcolor: '#ffd700', color: '#00695c', fontWeight: 800 }}>P</Avatar>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
              KAOZYA ENGLISH MEDIUM TEACHER PORTAL
            </Typography>
            <Typography variant="caption" sx={{ color: '#ffd700' }}>
              Logged in as: {user.full_name || user.username || 'Teacher'} ({user.username || 'teacher'})
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => window.location.href = '/admin/marks-entry'}
            sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.4)', fontWeight: 700, textTransform: 'none', borderRadius: 2 }}
          >
            Secondary Marks
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={() => window.location.href = '/admin/primary-marks-entry'}
            sx={{ bgcolor: '#ffd700', color: '#004d40', fontWeight: 800, textTransform: 'none', borderRadius: 2, '&:hover': { bgcolor: '#ffea00' } }}
          >
            Primary Marks
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setPasswordModal(true)}
            sx={{ color: '#ffd700', borderColor: '#ffd700', fontWeight: 700, textTransform: 'none', borderRadius: 2 }}
          >
            Change Password
          </Button>
          <Button
            variant="contained"
            size="small"
            color="error"
            onClick={() => { localStorage.clear(); window.location.href = '/admin/login'; }}
            sx={{ fontWeight: 700, textTransform: 'none', borderRadius: 2 }}
          >
            Sign Out
          </Button>
        </Box>
      </Paper>

      <StyledPaper>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a237e' }}>
              Primary School Marks Management
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Enter marks or view student results (Pre-school to STD VII)
            </Typography>
          </Box>
        </Box>

        {/* Tabs */}
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ mb: 3 }}>
          <Tab label="Enter Marks" icon={<Grade />} iconPosition="start" />
          <Tab label="View Results" icon={<Visibility />} iconPosition="start" />
        </Tabs>

        {/* Tab 0: Enter Marks */}
        {activeTab === 0 && (
          <>
            <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            {/* Step 1: Search Student */}
            {activeStep === 0 && (
              <Box>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Card sx={{ p: 3, bgcolor: '#e3f2fd' }}>
                      <Typography variant="h6" gutterBottom>
                        <Search sx={{ mr: 1, verticalAlign: 'middle' }} />
                        Search by Registration Number
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                        <TextField
                          fullWidth
                          label="Registration Number"
                          placeholder="e.g., PRI/2026/0001"
                          value={searchReg}
                          onChange={(e) => setSearchReg(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && handleSearchStudent()}
                        />
                        <Button
                          variant="contained"
                          onClick={handleSearchStudent}
                          disabled={loading}
                          sx={{ minWidth: 120 }}
                        >
                          {loading ? <CircularProgress size={24} /> : 'Search'}
                        </Button>
                      </Box>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Card sx={{ p: 3, bgcolor: '#fff3e0' }}>
                      <Typography variant="h6" gutterBottom>
                        <People sx={{ mr: 1, verticalAlign: 'middle' }} />
                        Or Select from Class
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                        <FormControl fullWidth>
                          <InputLabel>Select Class</InputLabel>
                          <Select
                            value={selectedClass}
                            onChange={(e) => setSelectedClass(e.target.value)}
                            label="Select Class"
                          >
                            <MenuItem value="Pre-school">Pre-school</MenuItem>
                            <MenuItem value="STD I">STD I</MenuItem>
                            <MenuItem value="STD II">STD II</MenuItem>
                            <MenuItem value="STD III">STD III</MenuItem>
                            <MenuItem value="STD IV">STD IV</MenuItem>
                            <MenuItem value="STD V">STD V</MenuItem>
                            <MenuItem value="STD VI">STD VI</MenuItem>
                            <MenuItem value="STD VII">STD VII</MenuItem>
                          </Select>
                        </FormControl>
                        <Button
                          variant="outlined"
                          onClick={handleGetClassList}
                          sx={{ minWidth: 120 }}
                        >
                          Load Students
                        </Button>
                      </Box>
                    </Card>
                  </Grid>
                </Grid>

                {students.length > 0 && (
                  <TableContainer component={Paper} sx={{ mt: 3 }}>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#1a237e' }}>
                          <StyledTableCell>Reg Number</StyledTableCell>
                          <StyledTableCell>Student Name</StyledTableCell>
                          <StyledTableCell>Class</StyledTableCell>
                          <StyledTableCell>Type</StyledTableCell>
                          <StyledTableCell align="center">Action</StyledTableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {students.map((s) => (
                          <TableRow key={s.id} hover>
                            <TableCell>{s.registration_number}</TableCell>
                            <TableCell>{s.full_name}</TableCell>
                            <TableCell>{s.current_class}</TableCell>
                            <TableCell>
                              <Chip label={s.student_type} size="small" color="primary" />
                            </TableCell>
                            <TableCell align="center">
                              <Button
                                size="small"
                                variant="contained"
                                onClick={async () => {
                                  setSelectedStudent(s);
                                  await loadExistingMarks(s.id);
                                  setActiveStep(1);
                                }}
                              >
                                Enter Marks
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            )}

            {/* Step 2: Enter Marks */}
            {activeStep === 1 && selectedStudent && (
              <Box>
                <Card sx={{ mb: 3, bgcolor: '#e8eaf6' }}>
                  <CardContent>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={4}>
                        <Typography variant="body2" color="text.secondary">Registration Number</Typography>
                        <Typography variant="h6">{selectedStudent.registration_number}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Typography variant="body2" color="text.secondary">Student Name</Typography>
                        <Typography variant="h6">{selectedStudent.full_name}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Typography variant="body2" color="text.secondary">Class</Typography>
                        <Typography variant="h6">{selectedStudent.current_class}</Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>

                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Term</InputLabel>
                      <Select value={term} onChange={(e) => {
                        setTerm(e.target.value);
                        if (selectedStudent) loadExistingMarks(selectedStudent.id);
                      }} label="Term">
                        <MenuItem value="Term 1">Term 1 (Jan - Mar)</MenuItem>
                        <MenuItem value="Term 2">Term 2 (Apr - Jun)</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Academic Year"
                      value={academicYear}
                      onChange={(e) => {
                        setAcademicYear(e.target.value);
                        if (selectedStudent) loadExistingMarks(selectedStudent.id);
                      }}
                    />
                  </Grid>
                </Grid>

                <Card sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5' }}>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                    <Add sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Add Subject
                  </Typography>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={8}>
                      <FormControl fullWidth>
                        <InputLabel>Select Subject</InputLabel>
                        <Select
                          value={newSubject}
                          onChange={(e) => setNewSubject(e.target.value)}
                          label="Select Subject"
                        >
                          {getAvailableSubjectsForStudent().map(subject => (
                            <MenuItem key={subject} value={subject}>{subject}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Button fullWidth variant="contained" startIcon={<Add />} onClick={handleAddSubject}>
                        Add Subject
                      </Button>
                    </Grid>
                  </Grid>
                </Card>

                {marks.length > 0 ? (
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#1a237e' }}>
                          <StyledTableCell>Subject</StyledTableCell>
                          <StyledTableCell align="center">Marks Obtained</StyledTableCell>
                          <StyledTableCell align="center">Total Marks</StyledTableCell>
                          <StyledTableCell align="center">Percentage</StyledTableCell>
                          <StyledTableCell align="center">Grade</StyledTableCell>
                          <StyledTableCell>Remarks</StyledTableCell>
                          <StyledTableCell align="center">Action</StyledTableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {marks.map((mark, index) => (
                          <TableRow key={index} hover>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <MenuBook sx={{ fontSize: 18, color: '#1a237e' }} />
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>{mark.subject_name}</Typography>
                              </Box>
                            </TableCell>
                            <TableCell align="center">
                              <TextField size="small" type="number" value={mark.marks_obtained}
                                onChange={(e) => handleMarkChange(index, 'marks_obtained', e.target.value)}
                                inputProps={{ min: 0, max: mark.total_marks }} sx={{ width: 100 }} />
                            </TableCell>
                            <TableCell align="center">
                              <TextField size="small" type="number" value={mark.total_marks}
                                onChange={(e) => handleMarkChange(index, 'total_marks', e.target.value)}
                                inputProps={{ min: 1 }} sx={{ width: 80 }} />
                            </TableCell>
                            <TableCell align="center">
                              {mark.marks_obtained && (
                                <Chip label={`${((mark.marks_obtained / mark.total_marks) * 100).toFixed(1)}%`}
                                  size="small" color={((mark.marks_obtained / mark.total_marks) * 100) >= 50 ? 'success' : 'error'} />
                              )}
                            </TableCell>
                            <TableCell align="center">
                              {mark.grade && (
                                <Chip label={mark.grade} size="small"
                                  color={mark.grade === 'A' ? 'success' : mark.grade === 'B' ? 'primary' : mark.grade === 'C' ? 'info' : mark.grade === 'D' ? 'warning' : 'error'} />
                              )}
                            </TableCell>
                            <TableCell>
                              <TextField size="small" placeholder="Remarks" value={mark.remarks}
                                onChange={(e) => handleMarkChange(index, 'remarks', e.target.value)} sx={{ width: 150 }} />
                            </TableCell>
                            <TableCell align="center">
                              <IconButton color="error" onClick={() => handleRemoveSubject(index)} size="small">
                                <Delete />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Alert severity="info" sx={{ mt: 2 }}>No subjects added. Click "Add Subject" to enter marks for a subject.</Alert>
                )}

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                  <Button variant="outlined" onClick={handleReset}>Back</Button>
                  <Button variant="contained" onClick={handleSaveMarks} disabled={saving || marks.length === 0}
                    startIcon={saving ? <CircularProgress size={20} /> : <Save />}>
                    {saving ? 'Saving...' : 'Save Marks'}
                  </Button>
                </Box>
              </Box>
            )}

            {/* Step 3: Success */}
            {activeStep === 2 && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <CheckCircle sx={{ fontSize: 80, color: '#4caf50', mb: 2 }} />
                <Typography variant="h4" gutterBottom>Marks Saved Successfully!</Typography>
                <Typography variant="body1" color="text.secondary" paragraph>
                  Student: {selectedStudent?.full_name} <br />
                  Class: {selectedStudent?.current_class} | {term} {academicYear}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 3 }}>
                  <Button variant="contained" onClick={handleReset}>Enter Another Student</Button>
                  <Button variant="outlined" onClick={() => window.print()}>Print Result Slip</Button>
                </Box>
              </Box>
            )}
          </>
        )}

        {/* Tab 1: View Results */}
        {activeTab === 1 && (
          <Box>
            <Card sx={{ p: 3, bgcolor: '#e3f2fd', mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                <Search sx={{ mr: 1, verticalAlign: 'middle' }} />
                Search Student to View Results
              </Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={8}>
                  <TextField
                    fullWidth
                    label="Registration Number"
                    placeholder="e.g., PRI/2026/0001"
                    value={viewSearchReg}
                    onChange={(e) => setViewSearchReg(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleViewSearch()}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleViewSearch}
                    disabled={viewLoading || !viewSearchReg.trim()}
                    startIcon={viewLoading ? <CircularProgress size={18} /> : <Search />}
                    sx={{ py: 1.5 }}
                  >
                    {viewLoading ? 'Searching...' : 'View Results'}
                  </Button>
                </Grid>
              </Grid>
            </Card>

            {viewStudent && (
              <Box>
                {/* Student Info */}
                <Card sx={{ mb: 3, bgcolor: '#e8eaf6' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ width: 60, height: 60, bgcolor: '#1a237e', fontSize: 28 }}>
                          {(viewStudent.full_name || '').charAt(0).toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography variant="h5" sx={{ color: '#1a237e', fontWeight: 700 }}>
                            {viewStudent.full_name}
                          </Typography>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                            {viewStudent.registration_number}
                          </Typography>
                          <Chip size="small" icon={<School fontSize="small" />}
                            label={viewStudent.current_class}
                            sx={{ mt: 0.5, bgcolor: `#1a237e15`, color: '#1a237e' }} />
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Refresh">
                          <IconButton onClick={handleViewSearch} sx={{ color: '#1a237e' }}>
                            <Refresh />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>

                {/* Term Selector */}
                {studentTerms.length > 0 && (
                  <Card sx={{ mb: 3 }}>
                    <CardContent>
                      <FormControl fullWidth>
                        <InputLabel>Select Term</InputLabel>
                        <Select value={selectedViewTerm} onChange={e => setSelectedViewTerm(e.target.value)} label="Select Term">
                          {studentTerms.map(term => (
                            <MenuItem key={term} value={term}>{term}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </CardContent>
                  </Card>
                )}

                {/* Results Summary Cards */}
                {filteredResults.length > 0 && (
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={4}>
                      <Card sx={{ p: 2, textAlign: 'center', bgcolor: '#e8f5e9' }}>
                        <Typography variant="h4" sx={{ color: '#2e7d32', fontWeight: 700 }}>{filteredResults.length}</Typography>
                        <Typography variant="body2">Subjects</Typography>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Card sx={{ p: 2, textAlign: 'center', bgcolor: '#e3f2fd' }}>
                        <Typography variant="h4" sx={{ color: '#1565c0', fontWeight: 700 }}>
                          {resultTotalObt.toFixed(1)}/{resultTotalPoss.toFixed(1)}
                        </Typography>
                        <Typography variant="body2">Total Marks</Typography>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Card sx={{ p: 2, textAlign: 'center', bgcolor: '#fff8e1' }}>
                        <Typography variant="h4" sx={{ color: '#f57f17', fontWeight: 700 }}>
                          {resultAvgPct.toFixed(1)}%
                        </Typography>
                        <Typography variant="body2">Average</Typography>
                      </Card>
                    </Grid>
                  </Grid>
                )}

                {/* Results Table */}
                {filteredResults.length > 0 ? (
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#1a237e' }}>
                          <TableCell sx={{ color: 'white', fontWeight: 700 }}>Subject</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 700 }} align="center">Marks Obtained</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 700 }} align="center">Total Marks</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 700 }} align="center">Percentage</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 700 }} align="center">Grade</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 700 }}>Remarks</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredResults.map((result, idx) => {
                          const g = calculateGrade(result.percentage);
                          return (
                            <TableRow key={idx} hover>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <MenuBook fontSize="small" sx={{ color: '#1a237e' }} />
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{result.subject_name}</Typography>
                                </Box>
                              </TableCell>
                              <TableCell align="center">{result.marks_obtained}</TableCell>
                              <TableCell align="center">{result.total_marks}</TableCell>
                              <TableCell align="center">{result.percentage}%</TableCell>
                              <TableCell align="center">
                                <Chip label={g} size="small" sx={{ fontWeight: 700, minWidth: 36 }} />
                              </TableCell>
                              <TableCell>{result.remarks || '—'}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : viewResults && (
                  <Alert severity="info">No results found for the selected term.</Alert>
                )}
              </Box>
            )}
          </Box>
        )}
      </StyledPaper>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    
      {/* ── Change Password Dialog ── */}
      <Dialog open={passwordModal} onClose={() => setPasswordModal(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ bgcolor: '#00695c', color: 'white', fontWeight: 800 }}>
          Change Password
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {passwordError && (
            <Alert severity="error" sx={{ mb: 2, mt: 1 }}>{passwordError}</Alert>
          )}
          <TextField
            fullWidth
            type="password"
            label="Current Password"
            value={passwordData.current_password}
            onChange={e => setPasswordData({ ...passwordData, current_password: e.target.value })}
            sx={{ mt: 1, mb: 2 }}
          />
          <TextField
            fullWidth
            type="password"
            label="New Password"
            value={passwordData.new_password}
            onChange={e => setPasswordData({ ...passwordData, new_password: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            type="password"
            label="Confirm New Password"
            value={passwordData.confirm_password}
            onChange={e => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
            sx={{ mb: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setPasswordModal(false)} color="inherit">Cancel</Button>
          <Button variant="contained" onClick={handleChangePassword} sx={{ bgcolor: '#00695c', fontWeight: 700 }}>
            Update Password
          </Button>
        </DialogActions>
      </Dialog>
    
</Container>
  );
};

export default PrimaryTeacherMarksEntry;